// src/features/secret/KvBrowser.tsx
// KV secret browser for a selected KV engine — path tree, version history, write, delete /
// undelete / destroy versions, metadata. Values are masked until explicitly revealed and never
// logged or left visible after navigating away (each reveal is per-version, cleared on path change).

import { useEffect, useState, useCallback } from 'react';
import { useI18n, useSecretKv } from '@react/hooks';
import { secretApi } from '@core/api';
import { Button, Input, ErrorAlert } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { TypedConfirmDialog } from './TypedConfirmDialog';
import { FormDialog, FormRow } from '@/components/form';
import type { KvSecret, KvSecretVersion } from '@core/types/secret';
import { buildPathTree, formatDateTime, validateKvData } from './logic';
import type { PathTreeNode } from './logic';

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);
  return { copied, copy };
}

// --- Path tree (left) ---

function TreeNode({ node, selectedPath, onSelect, depth }: {
  node: PathTreeNode; selectedPath: string | null; onSelect: (p: string) => void; depth: number;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        onClick={() => (node.isLeaf ? onSelect(node.fullPath) : setOpen((v) => !v))}
        className={`w-full text-left px-2 py-1 text-sm rounded hover:bg-muted/50 ${selectedPath === node.fullPath ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground'}`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
      >
        {node.children.length > 0 ? (open ? '▾ ' : '▸ ') : node.isLeaf ? '' : '▸ '}
        {node.name}
      </button>
      {open && node.children.map((child) => (
        <TreeNode key={child.fullPath} node={child} selectedPath={selectedPath} onSelect={onSelect} depth={depth + 1} />
      ))}
    </div>
  );
}

function KvPathList({ engineId, selectedPath, onSelect, reloadToken }: {
  engineId: string; selectedPath: string | null; onSelect: (p: string) => void; reloadToken: number;
}) {
  const { t } = useI18n();
  const [prefix, setPrefix] = useState('');
  const [paths, setPaths] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newPath, setNewPath] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    secretApi.ListKvSecrets(engineId, prefix ? { prefix } : undefined)
      .then((resp) => setPaths((resp.secrets ?? []).map((s: KvSecret) => s.path)))
      .catch(() => setPaths([]))
      .finally(() => setLoading(false));
  }, [engineId, prefix]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const tree = buildPathTree(paths);

  return (
    <div className="w-64 flex-shrink-0 border-r border-border flex flex-col h-full">
      <div className="p-2 border-b border-border space-y-2">
        <div className="flex items-center gap-1">
          <Input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder={t('secret.kv.searchPlaceholder')}
            aria-label={t('secret.kv.searchPlaceholder')}
            fullWidth
          />
          <Button size="sm" variant="secondary" onClick={() => setShowNew((v) => !v)} aria-label={t('secret.kv.newPathLabel')}>+</Button>
        </div>
        {showNew && (
          <div className="flex items-center gap-1">
            <Input
              value={newPath}
              onChange={(e) => setNewPath(e.target.value)}
              placeholder="my-app/config"
              fullWidth
            />
            <Button
              size="sm"
              onClick={() => { if (newPath.trim()) { onSelect(newPath.trim()); setNewPath(''); setShowNew(false); } }}
            >
              {t('common.create')}
            </Button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {loading ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : tree.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">{t('secret.kv.empty')}</p>
        ) : (
          tree.map((node) => (
            <TreeNode key={node.fullPath} node={node} selectedPath={selectedPath} onSelect={onSelect} depth={0} />
          ))
        )}
      </div>
    </div>
  );
}

// --- Write new version form ---

function WriteVersionForm({ open, engineId, path, onWritten, onClose }: {
  open: boolean; engineId: string; path: string; onWritten: () => void; onClose: () => void;
}) {
  const { t } = useI18n();
  const store = useSecretKv();
  const [entries, setEntries] = useState<Array<{ key: string; value: string }>>([{ key: '', value: '' }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const dirty = entries.some((e) => e.key.trim() || e.value.trim());

  const reset = () => {
    setEntries([{ key: '', value: '' }]);
    setErrors({});
    setSubmitError(null);
  };

  const close = () => { reset(); onClose(); };

  const updateEntry = (idx: number, field: 'key' | 'value', value: string) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };
  const removeEntry = (idx: number) => setEntries((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    const validationErrors = validateKvData(entries);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const data: Record<string, string> = {};
      for (const entry of entries) if (entry.key.trim()) data[entry.key.trim()] = entry.value;
      await store.PutKvSecret(engineId, path, { engine_id: engineId, path, data });
      reset();
      onWritten();
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      onSubmit={submit}
      submitLabel={t('secret.kv.writeSubmit')}
      canSubmit={entries.some((e) => e.key.trim())}
      isSubmitting={isSubmitting}
      dirty={dirty}
      aria-label={t('secret.kv.writeTitle')}
      context={<span className="rounded-md bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground">{path}</span>}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        {submitError != null && <ErrorAlert error={submitError} dismissible onDismiss={() => setSubmitError(null)} />}
        {errors.data && <p className="text-xs text-destructive">{t(errors.data)}</p>}
        <div className="space-y-2">
          {entries.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={entry.key}
                onChange={(e) => updateEntry(idx, 'key', e.target.value)}
                placeholder={t('secret.kv.keyPlaceholder')}
                fullWidth={false}
                className="flex-1"
                data-autofocus={idx === 0 ? '' : undefined}
              />
              <Input value={entry.value} onChange={(e) => updateEntry(idx, 'value', e.target.value)} placeholder={t('secret.kv.valuePlaceholder')} fullWidth={false} className="flex-1" type="password" />
              <Button type="button" variant="ghost" size="sm" onClick={() => removeEntry(idx)}>{t('common.remove')}</Button>
            </div>
          ))}
          <Button type="button" variant="secondary" size="sm" onClick={() => setEntries((prev) => [...prev, { key: '', value: '' }])}>
            {t('secret.kv.addField')}
          </Button>
        </div>
      </div>
    </FormDialog>
  );
}

// --- Reveal-on-demand value row ---

function RevealedVersion({ engineId, path, version }: { engineId: string; path: string; version: KvSecretVersion }) {
  const { t } = useI18n();
  const [revealed, setRevealed] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(false);
  const { copied, copy } = useCopyToClipboard();

  const reveal = async () => {
    setLoading(true);
    try {
      // Direct API call (not the store) — the store's GetKvSecret wrapper always reads the
      // latest version; the version query param is needed to reveal a specific history entry.
      const resp = await secretApi.GetKvSecret(engineId, path, { version: version.version });
      setRevealed(resp.secret?.version?.data ?? {});
    } finally {
      setLoading(false);
    }
  };

  if (!revealed) {
    return (
      <Button variant="ghost" size="sm" onClick={reveal} disabled={loading}>
        {loading ? t('common.loading') : t('secret.kv.reveal')}
      </Button>
    );
  }

  const entries = Object.entries(revealed);
  return (
    <div className="mt-2 space-y-1 rounded-md bg-muted/40 p-2">
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('secret.kv.noData')}</p>
      ) : entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs font-mono">
          <span className="text-muted-foreground min-w-[6rem]">{k}</span>
          <code className="flex-1 break-all select-all text-foreground">{v}</code>
          <button className="text-muted-foreground hover:text-foreground" onClick={() => copy(v)} aria-label={t('common.copy')}>
            {copied ? t('common.copied') : t('common.copy')}
          </button>
        </div>
      ))}
      <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setRevealed(null)}>
        {t('secret.kv.hide')}
      </button>
    </div>
  );
}

// --- Metadata edit form ---

function MetadataForm({ engineId, path, secret, onSaved, onCancel }: {
  engineId: string; path: string; secret: KvSecret; onSaved: () => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  const store = useSecretKv();
  // Empty = unset (no cap on retained versions), matching the "clearable to empty" field rule —
  // 0 and "no limit" aren't the same value here, so we don't coerce a blank input to 0 on blur.
  const [maxVersions, setMaxVersions] = useState(secret.max_versions ? String(secret.max_versions) : '');
  const [casRequired, setCasRequired] = useState(secret.cas_required);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const parsed = maxVersions.trim() === '' ? 0 : parseInt(maxVersions, 10);
      await store.UpdateKvSecretMetadata(engineId, path, {
        engine_id: engineId,
        path,
        max_versions: Number.isNaN(parsed) ? 0 : parsed,
        cas_required: casRequired,
        update_mask: ['max_versions', 'cas_required'],
      });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border p-3">
      <FormRow label={t('secret.kv.maxVersions')} labelWidth="sm">
        <Input
          value={maxVersions}
          onChange={(e) => setMaxVersions(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder={t('secret.kv.maxVersionsPlaceholder')}
          inputMode="numeric"
          fullWidth={false}
          className="w-28"
        />
      </FormRow>
      <FormRow label={t('secret.kv.casRequired')} labelWidth="sm">
        <input type="checkbox" checked={casRequired} onChange={(e) => setCasRequired(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" aria-label={t('secret.kv.casRequired')} />
      </FormRow>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={isSaving}>{t('common.cancel')}</Button>
        <Button size="sm" onClick={handleSave} disabled={isSaving}>{isSaving ? t('common.saving') : t('common.save')}</Button>
      </div>
    </div>
  );
}

// --- Secret detail (versions + metadata) ---

function KvSecretDetail({ engineId, path, onChanged }: { engineId: string; path: string; onChanged: () => void }) {
  const { t } = useI18n();
  const store = useSecretKv();
  const [secret, setSecret] = useState<KvSecret | null>(null);
  const [versions, setVersions] = useState<KvSecretVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWrite, setShowWrite] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  const [destroyTarget, setDestroyTarget] = useState<number | null>(null);
  const [isActing, setIsActing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    store.GetKvSecretMetadata(engineId, path)
      .then((resp) => { setSecret(resp.secret ?? null); setVersions(resp.versions ?? []); })
      .catch(() => { setSecret(null); setVersions([]); })
      .finally(() => setLoading(false));
  }, [engineId, path, store]);

  useEffect(() => { load(); setShowWrite(false); }, [load]);

  const doDeleteLatest = async () => {
    setIsActing(true);
    try { await store.DeleteKvSecret(engineId, path); load(); onChanged(); } finally { setIsActing(false); }
  };
  const doUndelete = async (version: number) => {
    setIsActing(true);
    try { await store.UndeleteKvSecretVersions(engineId, path, { engine_id: engineId, path, versions: [version] }); load(); } finally { setIsActing(false); }
  };
  const doDestroy = async () => {
    if (destroyTarget == null) return;
    setIsActing(true);
    try {
      await store.DestroyKvSecretVersions(engineId, path, { engine_id: engineId, path, versions: [destroyTarget] });
      setDestroyTarget(null);
      load();
    } finally {
      setIsActing(false);
    }
  };

  if (loading) return <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>;

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground font-mono">{path}</h2>
          <p className="text-xs text-muted-foreground">
            {t('secret.kv.currentVersion', { version: secret?.current_version ?? 0 })}
            {secret?.cas_required ? ` · ${t('secret.kv.casRequired')}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowWrite(true)}>{t('secret.kv.writeNewVersion')}</Button>
          {secret && <Button size="sm" variant="secondary" onClick={() => setShowMetadata((v) => !v)}>{t('secret.kv.editMetadata')}</Button>}
          {secret && <Button size="sm" variant="ghost" className="text-destructive" onClick={doDeleteLatest} disabled={isActing}>{t('secret.kv.deleteLatest')}</Button>}
        </div>
      </div>

      <WriteVersionForm
        open={showWrite}
        engineId={engineId}
        path={path}
        onWritten={() => { setShowWrite(false); load(); onChanged(); }}
        onClose={() => setShowWrite(false)}
      />

      {showMetadata && secret && (
        <MetadataForm engineId={engineId} path={path} secret={secret} onSaved={() => { setShowMetadata(false); load(); }} onCancel={() => setShowMetadata(false)} />
      )}

      <TypedConfirmDialog
        open={destroyTarget != null}
        title={t('secret.kv.destroyTitle')}
        description={t('secret.kv.destroyDescription')}
        phrase={`v${destroyTarget}`}
        confirmLabel={t('secret.kv.destroy')}
        isConfirming={isActing}
        onConfirm={doDestroy}
        onCancel={() => setDestroyTarget(null)}
      />

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">{t('secret.kv.versions')}</h3>
        {versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('secret.kv.noVersions')}</p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {versions.map((v) => (
              <li key={v.version} className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">v{v.version}</span>
                    {v.destroyed && <StatusBadge variant="error" label={t('secret.kv.destroyed')} size="sm" />}
                    {!v.destroyed && v.deletion_time && <StatusBadge variant="warning" label={t('secret.kv.deleted')} size="sm" />}
                    <span className="text-xs text-muted-foreground">{formatDateTime(v.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {!v.destroyed && !v.deletion_time && <RevealedVersion engineId={engineId} path={path} version={v} />}
                    {!v.destroyed && v.deletion_time && (
                      <Button variant="ghost" size="sm" onClick={() => doUndelete(v.version)} disabled={isActing}>{t('secret.kv.undelete')}</Button>
                    )}
                    {!v.destroyed && (
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDestroyTarget(v.version)}>{t('secret.kv.destroy')}</Button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// --- Top-level browser ---

export function KvBrowser({ engineId }: { engineId: string }) {
  const { t } = useI18n();
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  return (
    <div className="flex flex-1 min-h-0">
      <KvPathList engineId={engineId} selectedPath={selectedPath} onSelect={setSelectedPath} reloadToken={reloadToken} />
      {selectedPath ? (
        <KvSecretDetail engineId={engineId} path={selectedPath} onChanged={() => setReloadToken((n) => n + 1)} />
      ) : (
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-muted-foreground">{t('secret.kv.selectPath')}</p>
        </div>
      )}
    </div>
  );
}
